import React, { useCallback } from 'react';
import { Trash2, Clock, RotateCcw, ChevronRight } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import type { SlotGenerationHistoryEntry } from '../../../core/types/slot-asset-generator.types';

/** Renders a single saved-image thumbnail from a project-relative path. */
const HistoryThumb: React.FC<{ path: string }> = ({ path }) => {
  const { url } = useAssetUrl(path);
  if (!url) return <div className="w-10 h-10 rounded bg-zinc-800 flex-shrink-0" />;
  return (
    <img
      src={url}
      alt=""
      className="w-10 h-10 rounded object-contain bg-zinc-800 flex-shrink-0"
    />
  );
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface HistoryEntryCardProps {
  entry: SlotGenerationHistoryEntry;
  onRestore: (entry: SlotGenerationHistoryEntry) => void;
  onRemove: (id: string) => void;
}

const HistoryEntryCard: React.FC<HistoryEntryCardProps> = ({ entry, onRestore, onRemove }) => {
  return (
    <div className="group border border-zinc-700/50 rounded-lg p-2.5 hover:border-zinc-600 transition-colors">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-medium text-white truncate">
            {entry.componentName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-zinc-400">{formatTime(entry.timestamp)}</span>
          <button
            onClick={() => onRemove(entry.id)}
            className="p-0.5 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove from history"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Prompt text */}
      <p className="text-xs text-zinc-300 leading-relaxed mb-2 line-clamp-2">
        {entry.prompt}
      </p>

      {/* Thumbnails row */}
      {entry.savedPaths.length > 0 && (
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {entry.savedPaths.map((p, i) => (
            <HistoryThumb key={i} path={p} />
          ))}
        </div>
      )}

      {/* Meta + restore */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
          <span>{entry.model}</span>
          <span className="text-zinc-600">/</span>
          <span>{entry.style}</span>
          <span className="text-zinc-600">/</span>
          <span>{entry.quality}</span>
        </div>
        <button
          onClick={() => onRestore(entry)}
          className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors opacity-0 group-hover:opacity-100"
          title="Restore this prompt to the component"
        >
          <RotateCcw size={12} />
          Restore
        </button>
      </div>
    </div>
  );
};

export const SlotHistoryPanel: React.FC = () => {
  const history = useSlotAssetGeneratorStore((s) => s.history);
  const clearHistory = useSlotAssetGeneratorStore((s) => s.clearHistory);
  const removeHistoryEntry = useSlotAssetGeneratorStore((s) => s.removeHistoryEntry);
  const updateComponent = useSlotAssetGeneratorStore((s) => s.updateComponent);
  const selectComponent = useSlotAssetGeneratorStore((s) => s.selectComponent);
  const toggleHistoryPanel = useSlotAssetGeneratorStore((s) => s.toggleHistoryPanel);

  const handleRestore = useCallback((entry: SlotGenerationHistoryEntry) => {
    // Restore prompt to the component and select it
    updateComponent(entry.componentId, { prompt: entry.prompt });
    selectComponent(entry.componentId);
  }, [updateComponent, selectComponent]);

  const handleRemove = useCallback((id: string) => {
    removeHistoryEntry(id);
  }, [removeHistoryEntry]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Clock size={13} className="text-zinc-400" />
          <span className="text-xs font-medium text-zinc-300">History</span>
          {history.length > 0 && (
            <span className="text-[10px] text-zinc-600">({history.length})</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {history.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-[10px] text-zinc-600 hover:text-red-400 px-1.5 py-0.5 rounded hover:bg-zinc-800 transition-colors"
              title="Clear all history"
            >
              Clear
            </button>
          )}
          <button
            onClick={toggleHistoryPanel}
            className="p-1 text-zinc-500 hover:text-zinc-300 rounded hover:bg-zinc-800 transition-colors"
            title="Collapse history"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Entries */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-600 text-xs">
            <Clock size={24} className="mb-2 opacity-30" />
            <p>No history yet</p>
            <p className="text-[10px] mt-1">Save generated images to build history</p>
          </div>
        ) : (
          history.map((entry) => (
            <HistoryEntryCard
              key={entry.id}
              entry={entry}
              onRestore={handleRestore}
              onRemove={handleRemove}
            />
          ))
        )}
      </div>
    </div>
  );
};
